/**
 * Returns an object with the appropriate verb forms and pronoun based on the count and noun provided.
 *
 * @param  count - The number to determine singular or plural.
 * @param  noun - The noun to be pluralized if count is not 1.
 * @returns  An object containing the correct verb forms and pronoun:
 * - verbBe: 'is' if count is 1, otherwise 'are'.
 * -  verbHas: 'has' if count is 1, otherwise 'have'.
 * -  proNoun: 'it' if count is 1, otherwise 'them'.
 * -  pluralNoun: The noun itself if count is 1, otherwise the noun in its plural form.
 */
export const pluralize = (count: number, noun: string) => {
  const verbBe = count === 1 ? 'is' : 'are';
  const verbHas = count === 1 ? 'has' : 'have';
  const proNoun = count === 1 ? 'it' : 'them';
  const pluralNoun = count === 1 ? noun : `${noun}s`;

  return { verbBe, verbHas, pluralNoun, proNoun };
};

/**
 * Capitalizes the first letter of a string.
 *
 * @param str - The string to capitalize.
 * @returns A new string with the first character converted to uppercase and the rest unchanged.
 *
 * @example
 * ```typescript
 * capitalize("hello"); // Returns "Hello"
 * capitalize("world"); // Returns "World"
 * capitalize(""); // Returns "" (empty string)
 * ```
 */
export const capitalise = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Formats a given string into a standardized "data-cy" format.
 *
 * The function performs the following transformations:
 * - Converts the input string to lowercase.
 * - Replaces spaces and underscores with hyphens.
 * - Removes any special characters except hyphens, alphanumeric characters are preserved.
 *
 * @param input - The string to be formatted.
 * @returns The formatted string suitable for use as a "data-cy" attribute.
 *
 * @example
 * ```typescript
 * formatDataCy("Hello World"); // Returns "hello-world"
 * formatDataCy("My_Component_Name"); // Returns "my-component-name"
 * formatDataCy("Special@Chars!"); // Returns "specialchars"
 * ```
 */
export const formatDataCy = (input: string) => {
  // Convert to lowercase
  let formatted = input.toLowerCase();

  // Replace spaces, underscores, and any special characters with hyphens
  formatted = formatted.split(' ').join('-');
  formatted = formatted.split('_').join('-');
  formatted = formatted.split('.').join('-');

  // Remove any special characters except hyphens
  let cleanString = '';
  for (let i = 0; i < formatted.length; i++) {
    const char = formatted[i];
    if ((char >= 'a' && char <= 'z') || (char >= '0' && char <= '9') || char === '-') {
      cleanString += char;
    }
  }

  return cleanString;
};

export function formatSettingLabel(key: string): string {
  if (!key) return '';

  // Replace underscores and hyphens with spaces
  const spaced = key.replace(/[_-]+/g, ' ');

  // Capitalise the first letter only
  const capitalised = spaced.charAt(0).toUpperCase() + spaced.slice(1);

  return `${capitalised}`;
}
